import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';

import { Alert, Flex, Icon, Loader, Text } from '~/components/blocks';
import { ExternalLink } from '~/components/blocks/ExternalLink';
import { ReceptionUberDeliveryFragment } from '~/graphql/generated/graphql';
import { useFeatureFlag } from '~/hooks/use-feature-flag';
import { Label } from '~/utils/label';

import { useFetchUberCourier } from '../use-fetch_uber_courier';

type Props = {
  uberDelivery: ReceptionUberDeliveryFragment;
};

const Info = styled(Flex)(({ theme }) =>
  css({
    flexDirection: 'column',
    width: '100%',
    background: theme.colors.background.bg,
    marginTop: theme.space.s,
    padding: theme.space.m,
    position: 'relative',
  }),
);

const BoldSpan = styled('span')(() =>
  css({
    fontWeight: 'bold',
  }),
);

export const UberDeliveryField = React.memo((props: Props) => {
  const theme = useTheme();
  const { uberDelivery } = props;
  const uberDeliveryOrderNumber = uberDelivery.orderNumber;
  const deliveredAt = uberDelivery.deliveredAt;
  const { courier, loading } = useFetchUberCourier({
    uberDeliveryId: uberDelivery.id,
    status: uberDelivery.status,
  });

  const shouldShowOrderNumber =
    uberDelivery.status !== 'suspended' &&
    uberDelivery.status !== 'unfulfilled' &&
    uberDelivery.status !== 'before_dropoff_canceled';
  const shouldShowFee =
    uberDelivery.status === 'pending' ||
    uberDelivery.status === 'pickup' ||
    uberDelivery.status === 'dropoff';
  const shouldShowCourier = uberDelivery.status === 'pickup' || uberDelivery.status === 'dropoff';
  const shouldShowDeliveredAt = uberDelivery.status === 'delivered';
  const shouldShowCanceledHeading =
    uberDelivery.status === 'canceled' || uberDelivery.status === 'return_delivered';
  const shouldShowReturnedAt = uberDelivery.status === 'return_delivered';
  const shouldShowInfo =
    shouldShowOrderNumber ||
    shouldShowFee ||
    shouldShowCourier ||
    shouldShowDeliveredAt ||
    shouldShowCanceledHeading;
  const displayUberFreePromotion = useFeatureFlag('Issue9813-promotion');

  if (!uberDeliveryOrderNumber) {
    return null;
  }

  if (loading) {
    return (
      <Info>
        <Loader open inside logoSize="half" />
      </Info>
    );
  }

  return (
    <Flex flexDirection="column">
      {shouldShowCourier && !courier && (
        <Alert status="error">
          配達員の取得に失敗しました。
          <br />
          しばらく待ってから、再度アクセスしてください
        </Alert>
      )}
      {shouldShowInfo && (
        <Info>
          {shouldShowCanceledHeading && (
            <Text size="s" color="grey01">
              配達キャンセル
            </Text>
          )}
          {shouldShowOrderNumber && (
            <Text size="s">
              注文番号：<b>{uberDeliveryOrderNumber}</b>
            </Text>
          )}
          {shouldShowFee && <Text size="s">当日配達利用料：{uberDelivery.fee}円</Text>}
          {shouldShowCourier && courier && (
            <Text size="s">
              配達員：{courier.name}さん（{courier.phoneNumber}）
            </Text>
          )}
          {shouldShowDeliveredAt && deliveredAt && (
            <Text size="s">
              配達完了：{`${Label.YYYYMMDDja(deliveredAt)} ${Label.HHMM(deliveredAt)}`}
            </Text>
          )}
          {shouldShowReturnedAt && (
            <Text size="s">
              返送完了：
              {`${Label.YYYYMMDDja(uberDelivery.updatedAt)} ${Label.HHMM(uberDelivery.updatedAt)}`}
            </Text>
          )}
        </Info>
      )}
      {displayUberFreePromotion && (
        <Alert status="info" marginTop="s">
          <Text size="s">
            キャンペーン適用対象につき、今回の当日配達利用料は<BoldSpan>無料</BoldSpan>
            となります（
            <ExternalLink url="https://intercom.help/pharms/ja/articles/12539299">
              キャンペーン詳細
            </ExternalLink>
            <Icon
              icon="blank"
              size="m"
              marginLeft={theme.space.s}
              marginRight={theme.space.s}
              color="linkBlue"
            />
            ）
          </Text>
        </Alert>
      )}
    </Flex>
  );
});

UberDeliveryField.displayName = 'UberDeliveryField';
